<template>
  <div class="stomp-debug-panel" :class="{ collapsed: collapsed }">
    <div class="stomp-debug-header">
      <button class="stomp-debug-toggle" @click="toggleCollapsed">
        STOMP {{ summaryState }}
      </button>
      <button class="stomp-debug-refresh" @click="refreshSnapshot">刷新</button>
    </div>
    <div v-if="!collapsed" class="stomp-debug-body">
      <div class="stomp-debug-section">
        <div class="stomp-debug-row">
          <span>连接状态</span>
          <strong :class="statusClass">{{ summaryState }}</strong>
        </div>
        <div class="stomp-debug-row">
          <span>当前 URL</span>
          <strong>{{ snapshot.transport.currentUrl || '-' }}</strong>
        </div>
        <div class="stomp-debug-row">
          <span>Socket 状态</span>
          <strong>{{ snapshot.transport.socketReadyStateLabel || '-' }}</strong>
        </div>
        <div class="stomp-debug-row">
          <span>心跳</span>
          <strong>
            {{ snapshot.transport.configuredHeartbeat.outgoing }}/{{ snapshot.transport.configuredHeartbeat.incoming }} ms
          </strong>
        </div>
        <div class="stomp-debug-row">
          <span>Broker heart-beat</span>
          <strong>{{ snapshot.transport.brokerHeartbeat || '-' }}</strong>
        </div>
        <div class="stomp-debug-row">
          <span>Scope / Topic / 订阅</span>
          <strong>
            {{ snapshot.state.scopeCount }} / {{ snapshot.state.topicCount }} / {{ snapshot.state.subscriptionCount }}
          </strong>
        </div>
        <div class="stomp-debug-row">
          <span>消息数</span>
          <strong>{{ snapshot.metrics.messageCount }}</strong>
        </div>
        <div class="stomp-debug-row">
          <span>最近消息</span>
          <strong>{{ formatTime(snapshot.metrics.lastMessageAt) }}</strong>
        </div>
        <div class="stomp-debug-row">
          <span>最近断开</span>
          <strong>{{ snapshot.metrics.lastDisconnectReason || '-' }}</strong>
        </div>
        <div class="stomp-debug-row" v-if="snapshot.lastError">
          <span>最近错误</span>
          <strong class="error-text">{{ snapshot.lastError.type }}: {{ snapshot.lastError.message }}</strong>
        </div>
      </div>

      <details class="stomp-debug-section" open>
        <summary>Scopes ({{ snapshot.scopes.length }})</summary>
        <div v-if="!snapshot.scopes.length" class="stomp-empty">暂无 scope</div>
        <div v-for="scope in snapshot.scopes" :key="scope.id" class="stomp-debug-item">
          <div class="stomp-item-title">{{ scope.id }}</div>
          <div class="stomp-item-meta">
            {{ scope.metadata.componentName || scope.metadata.name || '-' }}
            <span v-if="scope.metadata.routePath">@ {{ scope.metadata.routePath }}</span>
          </div>
          <div class="stomp-item-meta">订阅数: {{ scope.subscriptionCount }}</div>
          <div v-for="subscription in scope.subscriptions" :key="subscription.id" class="stomp-sub-item">
            {{ subscription.destination }}
          </div>
        </div>
      </details>

      <details class="stomp-debug-section" open>
        <summary>Topics ({{ snapshot.topics.length }})</summary>
        <div v-if="!snapshot.topics.length" class="stomp-empty">暂无 topic</div>
        <div v-for="topic in snapshot.topics" :key="topic.key" class="stomp-debug-item">
          <div class="stomp-item-title">{{ topic.destination }}</div>
          <div class="stomp-item-meta">
            consumers: {{ topic.consumerCount }} / subscribed: {{ topic.subscribed ? 'yes' : 'no' }}
          </div>
          <div class="stomp-item-meta">最近消息: {{ formatTime(topic.lastMessageAt) }}</div>
        </div>
      </details>
    </div>
  </div>
</template>

<script>
import { globalStompService } from '@/utils/stomp';

function readCollapsedState() {
  return localStorage.getItem('stomp-debug-panel-collapsed') === '1';
}

export default {
  name: 'GlobalStompDebugPanel',
  data() {
    return {
      snapshot: globalStompService.getDebugSnapshot(),
      collapsed: readCollapsedState(),
      refreshTimer: null,
      removeStateListener: null,
      removeConnectedListener: null,
      removeDisconnectedListener: null,
      removeErrorListener: null,
      removeMessageListener: null,
    };
  },
  computed: {
    summaryState() {
      return (this.snapshot.state && this.snapshot.state.state) || 'unknown';
    },
    statusClass() {
      if (this.summaryState === 'connected') {
        return 'is-connected';
      }
      if (this.summaryState === 'reconnecting' || this.summaryState === 'connecting') {
        return 'is-pending';
      }
      return 'is-disconnected';
    },
  },
  mounted() {
    this.refreshSnapshot();
    this.refreshTimer = setInterval(this.refreshSnapshot, 1000);
    this.removeStateListener = globalStompService.addListener('statechange', this.refreshSnapshot);
    this.removeConnectedListener = globalStompService.addListener('connected', this.refreshSnapshot);
    this.removeDisconnectedListener = globalStompService.addListener('disconnected', this.refreshSnapshot);
    this.removeErrorListener = globalStompService.addListener('error', this.refreshSnapshot);
    this.removeMessageListener = globalStompService.addListener('message', this.refreshSnapshot);
  },
  beforeDestroy() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.removeStateListener) {
      this.removeStateListener();
      this.removeStateListener = null;
    }
    if (this.removeConnectedListener) {
      this.removeConnectedListener();
      this.removeConnectedListener = null;
    }
    if (this.removeDisconnectedListener) {
      this.removeDisconnectedListener();
      this.removeDisconnectedListener = null;
    }
    if (this.removeErrorListener) {
      this.removeErrorListener();
      this.removeErrorListener = null;
    }
    if (this.removeMessageListener) {
      this.removeMessageListener();
      this.removeMessageListener = null;
    }
  },
  methods: {
    refreshSnapshot() {
      this.snapshot = globalStompService.getDebugSnapshot();
    },
    toggleCollapsed() {
      this.collapsed = !this.collapsed;
      localStorage.setItem('stomp-debug-panel-collapsed', this.collapsed ? '1' : '0');
    },
    formatTime(timestamp) {
      if (!timestamp) {
        return '-';
      }
      const date = new Date(timestamp);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return hours + ':' + minutes + ':' + seconds;
    },
  },
};
</script>

<style scoped>
.stomp-debug-panel {
  position: fixed;
  right: 12px;
  bottom: 12px;
  z-index: 9999;
  width: 380px;
  max-height: calc(100vh - 24px);
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  font-size: 12px;
  color: #1f1f1f;
  overflow: hidden;
}

.stomp-debug-panel.collapsed {
  width: 180px;
}

.stomp-debug-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
}

.stomp-debug-toggle,
.stomp-debug-refresh {
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
}

.stomp-debug-toggle {
  font-weight: 600;
}

.stomp-debug-body {
  max-height: calc(100vh - 90px);
  overflow: auto;
  padding: 10px;
}

.stomp-debug-section {
  margin-bottom: 10px;
}

.stomp-debug-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
}

.stomp-debug-row span {
  color: #595959;
}

.stomp-debug-item {
  padding: 8px;
  margin-top: 8px;
  border-radius: 6px;
  background: #fafafa;
}

.stomp-item-title {
  font-weight: 600;
  word-break: break-all;
}

.stomp-item-meta,
.stomp-sub-item,
.stomp-empty {
  margin-top: 4px;
  color: #595959;
  word-break: break-all;
}

.stomp-sub-item {
  padding-left: 8px;
  border-left: 2px solid #d9d9d9;
}

.is-connected {
  color: #389e0d;
}

.is-pending {
  color: #d48806;
}

.is-disconnected,
.error-text {
  color: #cf1322;
}
</style>